import type { ConversationStatus } from "./conversation";
import type { ClickedAd } from "./message";
import type { OptOutSource } from "@/features/contact/types/contact";

export const ACTIVITY_TYPE = {
  CREATED: "created",
  ASSIGNED: "assigned",
  UNASSIGNED: "unassigned",
  LIFECYCLE_CHANGE: "lifecycle_change",
  CONVERSATION_STATUS_CHANGE: "conversation_status_change",
  SNOOZE: "snooze",
  UNSNOOZE: "unsnooze",
  BLOCK: "block",
  UNBLOCK: "unblock",
  NOTE: "note",
  ADS_CLICKED: "ads_clicked",
  WEBSITE_VISITED: "website_visited",
  FORM_SUBMITTED: "form_submitted",
  CALL: "call",
  ORDER_UPDATE: "order_update",
  FOLLOW_UP_SET: "follow_up_set",
  FOLLOW_UP_CLEARED: "follow_up_cleared",
  FOLLOW_UP_UPDATED: "follow_up_updated",
  FOLLOW_UP_COMPLETED: "follow_up_completed",
  FOLLOW_UP_DUE: "follow_up_due",
  APPOINTMENT_CREATED: "appointment_created",
  APPOINTMENT_UPDATED: "appointment_updated",
  APPOINTMENT_RESCHEDULED: "appointment_rescheduled",
  APPOINTMENT_DELETED: "appointment_deleted",
  APPOINTMENT_OUTCOME: "appointment_outcome",
  APPOINTMENT_BILLED: "appointment_billed",
  // Tag activities
  TAG_ADDED: "tag_added",
  TAG_REMOVED: "tag_removed",
  // Workflow activities
  WORKFLOW_STARTED: "workflow_started",
  WORKFLOW_ENDED: "workflow_ended",
  WORKFLOW_PAUSED: "workflow_paused",
  WORKFLOW_DELAYED: "workflow_delayed",
  // Handover activities
  HANDOVER_PARKED: "handover_parked",
  HANDOVER_EXHAUSTED: "handover_exhausted",
  AVAILABILITY_ROUTING: "availability_routing",
  // Echo pause activities
  AI_PAUSED: "ai_paused",
  AI_RESUMED: "ai_resumed",
  // A teammate-started Echo session, once it closed
  AI_SESSION: "ai_session",
  // Broadcasts stopped or restarted; normal chat is untouched
  OPT_OUT: "opt_out",
  OPT_IN: "opt_in",
} as const;

export type ActivityType = (typeof ACTIVITY_TYPE)[keyof typeof ACTIVITY_TYPE];

export const CALL_OUTCOME = {
  ANSWERED: "answered",
  MISSED: "missed",
  VOICEMAIL: "voicemail",
  BUSY: "busy",
  NO_ANSWER: "no_answer",
} as const;

export type CallOutcome = (typeof CALL_OUTCOME)[keyof typeof CALL_OUTCOME];

export const CALL_DIRECTION = {
  INBOUND: "inbound",
  OUTBOUND: "outbound",
  INCOMING: "incoming",
  OUTGOING: "outgoing",
} as const;

export type CallDirection =
  (typeof CALL_DIRECTION)[keyof typeof CALL_DIRECTION];

// Interfaces
interface BaseActivity {
  id: string;
  userId: string | null;
  contactId: string;
  mentioned: Array<string>;
  createdAt: string;
  owner?: "user" | "api" | "ai" | "system";
}

interface ActivityPayloadMap {
  assigned: AssigneeActivityPayload;
  unassigned: AssigneeActivityPayload;
  lifecycle_change: LifecycleActivityPayload;
  conversation_status_change: StatusActivityPayload;
  created: ContactCreatedActivityPayload;
  note: NoteActivityPayload;
  snooze: SnoozeActivityPayload;
  block: ContactBlockActivityPayload;
  unblock: ContactBlockActivityPayload;
  unsnooze: Record<string, never>;
  ads_clicked: ClickedAd;
  website_visited: WebsiteVisitedActivityPayload;
  form_submitted: FormSubmittedActivityPayload;
  call: CallActivityPayload;
  order_update: OrderUpdateActivityPayload;
  follow_up_set: FollowUpActivityPayload;
  follow_up_cleared: FollowUpActivityPayload;
  follow_up_updated: FollowUpUpdatedActivityPayload;
  follow_up_completed: FollowUpActivityPayload;
  follow_up_due: FollowUpDueActivityPayload;
  appointment_created: AppointmentActivityPayload;
  appointment_updated: AppointmentUpdatedActivityPayload;
  appointment_outcome: AppointmentOutcomeActivityPayload;
  appointment_billed: AppointmentBilledActivityPayload;
  appointment_rescheduled: AppointmentRescheduledActivityPayload;
  appointment_deleted: AppointmentActivityPayload;
  tag_added: TagActivityPayload;
  tag_removed: TagActivityPayload;
  workflow_started: WorkflowStartedPayload;
  workflow_ended: WorkflowEndedPayload;
  workflow_paused: WorkflowPausedPayload;
  workflow_delayed: WorkflowDelayedPayload;
  handover_parked: HandoverParkedPayload;
  handover_exhausted: HandoverExhaustedPayload;
  availability_routing: AvailabilityRoutingActivityPayload;
  ai_paused: AiPausedActivityPayload;
  ai_resumed: Record<string, never>;
  ai_session: AiSessionActivityPayload;
  opt_out: BroadcastOptActivityPayload;
  opt_in: BroadcastOptActivityPayload;
}

export type SnoozeActivityPayload = {
  snoozedUntil: string;
};

/** `until` is far in the future when the pause lasts until someone resumes it. */
export type AiPausedActivityPayload = {
  until: string;
};

export const AI_SESSION_OUTCOME = {
  SENT: "sent",
  DISCARDED: "discarded",
  STOPPED: "stopped",
  SUPERSEDED: "superseded",
  TAKEN_OVER: "taken_over",
  DONE: "done",
} as const;

export type AiSessionOutcome =
  (typeof AI_SESSION_OUTCOME)[keyof typeof AI_SESSION_OUTCOME];

/** Echo's runs toward one reply, written when they end and dated at the ask.
 *  `actions` are the tools that ran, as their display titles. */
export type AiSessionActivityPayload = {
  sessionId: string;
  outcome: AiSessionOutcome;
  runs: number;
  generationIds: string[];
  actions: string[];
  /** Echo's own time across the runs; absent on sessions recorded before it was stored. */
  durationMs?: number | null;
  /** The whole line, written by the backend: actions, then how it ended
   *  ("Chose not to reply"). Empty when the actions' own cards say it all;
   *  absent on sessions recorded before it was. */
  summary?: string;
  /** Why a session ended without a reply, in the model's own words. Model
   *  text: never HTML. */
  noReplyReason?: string;
};

export const ASSIGNMENT_REASON = {
  MANUAL: "manual",
  WORKFLOW: "workflow",
  HANDOVER: "handover",
  API: "api",
  REPLY_TAKEOVER: "reply_takeover",
  AI_HANDOFF: "ai_handoff",
  TAKEOVER: "takeover",
  APPOINTMENT_OUTCOME: "appointment_outcome",
  AVAILABILITY: "availability",
} as const;

export type AssignmentReason =
  (typeof ASSIGNMENT_REASON)[keyof typeof ASSIGNMENT_REASON];

export type AssigneeActivityPayload = {
  /** Null on an `unassigned` activity - the backend writes the new assignee
   *  straight through, and a release (or an AI transfer) has no human. */
  assignee: string | null;
  /** The AI took ownership. Routing models that as `assignee: null`, which is
   *  otherwise indistinguishable from a release - without this the row reads as
   *  an unassignment that never happened. */
  toAi?: boolean;
  /** The AI was switched off: a teammate took the conversation over, or the
   *  clock handed it back to them. The assignee is whoever holds it now. */
  fromAi?: boolean;
  /** Who lost the conversation. Only set on a real move (routing takes it from
   *  one owner and gives it to another in a single update), never on a first
   *  assignment or a genuine release. */
  previousAssignee?: string;
  /** Why the system made this assignment. Rendered as a short chip. */
  reason?: AssignmentReason;
  /** Free prose, human-authored (a transfer note) or the AI's own handoff
   *  reason. Its presence is what promotes the activity into the message
   *  stream, so nothing system-generated should be written here. */
  description?: string;
};

export type LifecycleActivityPayload = {
  lifecycleId: string;
  fromLifecycleId?: string;
  reason?: string;
  /** Set on the stage written with a contact created for a form fill. */
  formName?: string;
};

export type ConversationStatusChangeReason = "follow_up_due";

export type StatusActivityPayload = {
  status: ConversationStatus;
  reason?: ConversationStatusChangeReason;
  /** Set when a due follow-up reopened it; absent on older activities. */
  followUpId?: string;
  followUpReason?: string | null;
  closingNote?: {
    topicId: string;
    topicName: string;
    summary?: string;
    fieldValues?: Array<{
      fieldId: string;
      fieldName: string;
      type: string;
      value: Record<string, unknown>;
    }>;
  };
};

export type ContactCreatedActivityPayload = {
  userId?: string;
  // Audit note stamped when the contact originated from a channel, e.g.
  // "Synced from 'Acme WhatsApp'".
  description?: string;
  /** The form an integration created the contact for, seconds before posting the fill. */
  formName?: string;
  formSubmissionId?: string;
};

export type NoteActivityPayload = {
  text: string;
};

export type ContactBlockActivityPayload = {
  blocked: boolean;
  userId: string;
};

/** `userId` is set only when a teammate made the change. */
export type BroadcastOptActivityPayload = {
  source: OptOutSource | null;
  userId: string | null;
};

/** @see getBroadcastOptText */
export interface GetBroadcastOptTextInput {
  optedOut: boolean;
  source: OptOutSource | null;
  actionUserName: string | null;
  isCurrentUser: boolean;
  includeActor?: boolean;
}

export type CallTranscriptionLine = {
  speaker: "agent" | "customer";
  text: string;
  timestamp?: string;
};

export type CallActivityPayload = {
  direction: CallDirection;
  duration?: number;
  outcome?: CallOutcome;
  status?: CallOutcome;
  callerId?: string;
  recordingUrl?: string;
  notes?: string;
  transcription?: CallTranscriptionLine[];
};

export type OrderUpdateTarget = "delivery" | "payment";

export type OrderUpdateActivityPayload = {
  orderId: string;
  displayOrderId: string;
  target: OrderUpdateTarget;
  oldStatus: string | null;
  newStatus: string;
  createdAt: string;
};

export type WebsiteVisitedActivityPayload = {
  websiteId: string;
  websiteName: string;
  domain: string;
  url: string;
  pagePath?: string;
  pageTitle?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
};

export type FormSubmissionAnswer = {
  question: string;
  value: unknown;
};

export type FormSubmissionWebsiteVisit = {
  url: string | null;
  pagePath: string | null;
  pageTitle: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
};

export type FormSubmittedActivityPayload = {
  submissionId: string;
  formId: string;
  formName: string;
  source?: string;
  externalId?: string | null;
  adId?: string | null;
  // Attribution captured at submission time: the ad the contact clicked and the
  // website visit that preceded the fill, so agents can see how the lead arrived.
  adName?: string | null;
  websiteVisitId?: string | null;
  websiteVisit?: FormSubmissionWebsiteVisit | null;
  // Legacy shape: older activities stored just the visit URL.
  websiteUrl?: string | null;
  fields: FormSubmissionAnswer[];
  submittedAt?: string;
};

export type FollowUpActivityPayload = {
  followUpAt: string | null;
  followUpReason: string | null;
  source?: "user" | "workflow" | "system" | "ai";
  /** On a completion: the teammate's answer to "did this lead to the booking or order?". */
  skipWhatsapp?: boolean;
  archived?: boolean;
  archivedAt?: string;
};

/** Both fields are absent on activities written before they were stored. */
export type FollowUpDueActivityPayload = {
  followUpId?: string;
  reason?: string | null;
};

export type FollowUpUpdatedActivityPayload = FollowUpActivityPayload & {
  oldFollowUpReason?: string | null;
  oldFollowUpAt?: string | null;
};

export type AppointmentActivitySnapshot = {
  treatmentName?: string;
  doctorName?: string;
  bookingType?: string;
  visitType?: string | null;
  status?: string;
  startDate?: string;
  endDate?: string;
};

export type AppointmentActivityPayload = AppointmentActivitySnapshot & {
  appointmentId: string;
  startDate: string;
  endDate: string;
};

export type AppointmentChange = {
  field: string;
  previous?: unknown;
  new?: unknown;
};

export type AppointmentUpdatedActivityPayload = AppointmentActivitySnapshot & {
  appointmentId: string;
  changes: AppointmentChange[];
};

export type AppointmentOutcomeActivityPayload = AppointmentActivitySnapshot & {
  appointmentId: string;
  /** One of the five outcome answers, `still_deciding` included. */
  outcome: string;
  bookingType?: string;
  /** The closing note picked as why they passed, by the name it had then. */
  reason?: string;
};

export type AppointmentBilledActivityPayload = AppointmentActivitySnapshot & {
  appointmentId: string;
  /** The appointment's value once billed, as a 2dp string. */
  value?: string | null;
};

export type AppointmentRescheduledActivityPayload =
  AppointmentActivitySnapshot & {
    appointmentId: string;
    oldStartDate: string;
    oldEndDate: string;
    newStartDate: string;
    newEndDate: string;
    rescheduledCount: number;
  };

export type TagRef = {
  id: string;
  name: string;
};

export type TagActivityPayload = {
  /** Tags paired with their names. `reason` is only set by AI auto-tagging. */
  tags?: TagRef[];
  reason?: string;
};

export type WorkflowStartedPayload = {
  workflowId: string;
  workflowName: string;
  executionId: string;
};

export type WorkflowEndedPayload = {
  workflowId: string;
  workflowName: string;
  executionId: string;
  reason: "success" | "exited" | "cancelled" | "failed";
  exitReason?: string;
};

export type WorkflowPausedPayload = {
  workflowId: string;
  workflowName: string;
  executionId: string;
};

export type WorkflowDelayedPayload = {
  workflowId: string;
  workflowName: string;
  executionId: string;
  reason: "timer" | "waiting_input" | "waiting_reply" | "waiting_contact_reply";
  resumeAt?: string;
};

export type HandoverParkedReason = "outside_hours" | "no_online_agent";

export type HandoverParkedPayload = {
  reason: HandoverParkedReason;
  triedUserIds: string[];
};

export type HandoverExhaustedPayload = {
  triedUserIds: string[];
  poolSource: string;
};

// Mirrors the backend RoutingTrigger enum (src/routing/routing.enum.ts). These
// values are persisted on routing events, so they must match it exactly.
export type RoutingTrigger =
  | "assignment_requested"
  | "assignee_unavailable"
  | "message_response_overdue"
  | "follow_up_overdue";

export type RoutingOutcome =
  | "nudged"
  | "assigned"
  | "assigned_ai"
  | "waiting"
  | "parked"
  | "kept_current"
  | "fallback_workspace"
  | "exhausted";

export type RoutingCandidateDecision =
  | "selected"
  | "eligible"
  | "not_selected"
  | "offline"
  | "away"
  | "manual_away"
  | "excluded"
  | "already_tried";

export type AvailabilityRoutingCandidate = {
  userId: string;
  status: "online" | "away" | "offline";
  routable: boolean;
  source: string | null;
  absenceSessionId: string | null;
  decision: RoutingCandidateDecision;
  initialDecision?: RoutingCandidateDecision;
};

export type AvailabilityRoutingActivityPayload = {
  routingEventId: string;
  trigger: RoutingTrigger;
  outcome: RoutingOutcome;
  previousUserId: string | null;
  selectedUserId: string | null;
  candidates: AvailabilityRoutingCandidate[];
};

export type Activity = {
  [K in keyof ActivityPayloadMap]: BaseActivity & {
    type: K;
    payload: ActivityPayloadMap[K];
  };
}[keyof ActivityPayloadMap];

export type AiSessionActivity = Extract<
  Activity,
  { type: typeof ACTIVITY_TYPE.AI_SESSION }
>;

export type CreateActivityPayload = {
  [K in keyof ActivityPayloadMap]: Pick<
    BaseActivity,
    "contactId" | "mentioned"
  > & {
    id?: string;
    type: K;
    payload: ActivityPayloadMap[K];
  };
}[keyof ActivityPayloadMap];
